import { useEffect } from "react";
import { FDS_ActionTypes, FDS_SubscriptionTypes } from "constants/index";
import WebSocketWrapper from "../ws/WebSocketWrapper";
import {
  addSubscription,
  cacheMessage,
  removeSubscription,
} from "features/factset-streaming/factsetStreamingSlice";

/**
 * Custom hook for managing FDS WebSocket connections
 * Handles subscription, unsubscription, and cleanup for stock and option data
 *
 * @param stockSubKey - Subscription key for stock data (format: "HOMEPAGE_STOCK:symbol1,symbol2")
 * @param optionSubKey - Subscription key for option data (format: "HOMEPAGE_OPTION:symbol1,symbol2")
 * @param subscriptions - Current subscriptions object from Redux store
 * @param dispatch - Redux dispatch function
 */
export const useFDSWebSocket = (
  stockSubKey: string | null,
  optionSubKey: string | null,
  subscriptions: any,
  subType: FDS_SubscriptionTypes | undefined,
  dispatch: any,
) => {
  useEffect(() => {
    // Don't connect if no subscriptions needed
    if (!stockSubKey && !optionSubKey) {
      return;
    }

    const ws = new WebSocketWrapper();

    // Only connect if we don't already have these subscriptions
    if (
      !(
        (stockSubKey && stockSubKey in subscriptions) ||
        (optionSubKey && optionSubKey in subscriptions)
      )
    ) {
      ws.connect(process.env.REACT_APP_FDS_URL! + "/fds");

      ws.on("open", () => {
        if (subType && optionSubKey) {
          const [_, symbols] = optionSubKey.split(":");
          dispatch(addSubscription(optionSubKey));
          ws.send(
            JSON.stringify({
              action: FDS_ActionTypes.SUBSCRIBE,
              type: subType,
              symbol: symbols,
            }),
          );
        } else {
          // Subscribe to stock data
          if (stockSubKey && !(stockSubKey in subscriptions)) {
            const [_, symbols] = stockSubKey.split(":");
            dispatch(addSubscription(stockSubKey));
            ws.send(
              JSON.stringify({
                action: FDS_ActionTypes.SUBSCRIBE,
                type: FDS_SubscriptionTypes.HOMEPAGE_STOCK,
                symbol: symbols,
              }),
            );
          }

          // Subscribe to option data
          if (optionSubKey && !(optionSubKey in subscriptions)) {
            const [_, symbols] = optionSubKey.split(":");
            dispatch(addSubscription(optionSubKey));
            ws.send(
              JSON.stringify({
                action: FDS_ActionTypes.SUBSCRIBE,
                type: FDS_SubscriptionTypes.HOMEPAGE_OPTION,
                symbol: symbols,
              }),
            );
          }
        }
      });

      // Handle incoming messages
      ws.on("message", (ev: any) => {
        if (ev.data !== "ok") {
          dispatch(cacheMessage(JSON.parse(ev.data)));
        }
      });

      // Handle connection close
      ws.on("close", () => {
        if (stockSubKey) {
          dispatch(removeSubscription(stockSubKey));
        }
        if (optionSubKey) {
          dispatch(removeSubscription(optionSubKey));
        }
      });
    }

    // Cleanup function
    return () => {
      const isSocketOpen =
        ws &&
        (ws as any).socket &&
        (ws as any).socket.readyState === WebSocket.OPEN;

      if (subType && optionSubKey) {
        const [_, symbols] = optionSubKey.split(":");
        dispatch(removeSubscription(optionSubKey));
        if (isSocketOpen) {
          ws.send(
            JSON.stringify({
              action: FDS_ActionTypes.UNSUBSCRIBE,
              type: subType,
              symbol: symbols,
            }),
          );
        }
      } else {
        if (stockSubKey && stockSubKey in subscriptions) {
          const [_, symbols] = stockSubKey.split(":");
          dispatch(removeSubscription(stockSubKey));
          if (isSocketOpen) {
            ws.send(
              JSON.stringify({
                action: FDS_ActionTypes.UNSUBSCRIBE,
                type: FDS_SubscriptionTypes.HOMEPAGE_STOCK,
                symbol: symbols,
              }),
            );
          }
        }

        if (optionSubKey && optionSubKey in subscriptions) {
          const [_, symbols] = optionSubKey.split(":");
          dispatch(removeSubscription(optionSubKey));
          if (isSocketOpen) {
            ws.send(
              JSON.stringify({
                action: FDS_ActionTypes.UNSUBSCRIBE,
                type: FDS_SubscriptionTypes.HOMEPAGE_OPTION,
                symbol: symbols,
              }),
            );
          }
        }
      }
      ws.disconect();
    };
  }, [stockSubKey, optionSubKey]);
};
